import { takeEvery, takeLatest, put, call, all, select } from 'redux-saga/effects';
import Experience from '../experience';

import WinnerApi from './winner-api';
import WinnerActions from './winner-actions';
import WinnerSelectors from './winner-selectors';

import LegacyWinnersManagementInterface from './legacy-interfaces';

function* fetchDrawsHandler({ payload: { experienceId } }) {
  const { error, ...draws } = yield call(WinnerApi.fetchDraws, experienceId);

  if (error) yield put(WinnerActions.fetchFailure(error));
  else yield put(WinnerActions.fetchSuccess(draws));
}

function* shuffleDrawHandler({ payload: { id } }) {
  const { error, ...draw } = yield call(WinnerApi.shuffleDraw, id);

  if (error) {
    yield put(WinnerActions.updateDrawFailure(error));
    // TEMP FIX: if there was insufficent reserves to shuffle a draw, we will get a 200 code and should refresh the list of winners
    const experience = yield select(Experience.Selectors.experience);
    yield put(WinnerActions.fetch(experience.id));
  } else {
    yield put(WinnerActions.updateDrawSuccess(id, draw));
  }
}

function* reselectDrawHandler({ payload: { id } }) {
  const { error, ...draw } = yield call(WinnerApi.reselectDraw, id);

  if (error) {
    yield put(WinnerActions.updateDrawFailure(error));
    // TEMP FIX: if there was insufficent reserves to reselect a draw, we will get a 200 code and should refresh the list of winners
    const experience = yield select(Experience.Selectors.experience);
    yield put(WinnerActions.fetch(experience.id));
  } else {
    yield put(WinnerActions.updateDrawSuccess(id, draw));
  }
}

function* invalidateWinnerHandler({ payload: { id } }) {
  const { error, ...winner } = yield call(WinnerApi.invalidateWinner, id);

  if (error) yield put(WinnerActions.updateWinnerFailure(error));
  else yield put(WinnerActions.updateWinnerSuccess(winner));
}

function* validateWinnerHandler({ payload: { id } }) {
  const { error, ...winner } = yield call(WinnerApi.validateWinner, id);

  if (error) yield put(WinnerActions.updateWinnerFailure(error));
  else yield put(WinnerActions.updateWinnerSuccess(winner));
}

function* reselectWinnerHandler({ payload: { id } }) {
  // find the draw that contains that winner
  const { error, ...winner } = yield call(WinnerApi.reselectWinner, id);

  //on specific error patterns we still get an empty winner object which triggers a false positive, hence why we need to track for error first
  if (error) {
    yield put(WinnerActions.reselectWinnerFailure(error));
    // TEMP FIX: if there was insufficent reserves to reselect a winner, we will get a 200 code and should refresh the list of winners
    const experience = yield select(Experience.Selectors.experience);
    yield put(WinnerActions.fetch(experience.id));
  } else {
    const drawId = yield select(WinnerSelectors.drawIdByWinnerId, id, drawId);
    yield put(WinnerActions.reselectWinnerSuccess(winner, drawId, id));
  }
}

function* pickWinnersHandler() {
  const experience = yield select(Experience.Selectors.experience);
  //might have to inspect this one for
  yield call(LegacyWinnersManagementInterface.pickWinners, experience);
  yield put(WinnerActions.fetch(experience.id));
  yield put(WinnerActions.fetchWinnersHistory(experience.id));
}

function* downloadWinnersExportHandler() {
  const experience = yield select(Experience.Selectors.experience);
  yield call(LegacyWinnersManagementInterface.downloadWinnersExport, experience);
}

function* sendWinnersExportHandler() {
  const experience = yield select(Experience.Selectors.experience);
  yield call(LegacyWinnersManagementInterface.sendWinnersExport, experience);
}

function* fetchWinnersHistoryHandler({ payload: { experienceId } }) {
  const _experience = yield select(Experience.Selectors.experience);

  const { error, ...winnerHistorys } = yield call(WinnerApi.fetchWinnersHistory, experienceId || _experience.id);

  if (error) yield put(WinnerActions.fetchWinnersHistoryFailure(error));
  else yield put(WinnerActions.fetchWinnersHistorySuccess(winnerHistorys));
}

function* viewEntryHandler({ payload: { experienceId, entryId } }) {
  yield call(window.createEntryManagerApp, { experienceId, entryId });
}

export default function* winnerSagas() {
  yield all([
    takeLatest(WinnerActions.fetch.getType(), fetchDrawsHandler),
    takeEvery(WinnerActions.shuffleDraw.getType(), shuffleDrawHandler),
    takeEvery(WinnerActions.reselectDraw.getType(), reselectDrawHandler),
    takeEvery(WinnerActions.invalidateWinner.getType(), invalidateWinnerHandler),
    takeEvery(WinnerActions.validateWinner.getType(), validateWinnerHandler),
    takeEvery(WinnerActions.reselectWinner.getType(), reselectWinnerHandler),
    takeLatest(WinnerActions.pickWinners.getType(), pickWinnersHandler),
    takeLatest(WinnerActions.downloadWinnersExport.getType(), downloadWinnersExportHandler),
    takeLatest(WinnerActions.sendWinnersExport.getType(), sendWinnersExportHandler),
    takeLatest(WinnerActions.viewEntry.getType(), viewEntryHandler),
    takeLatest(
      [
        WinnerActions.fetchWinnersHistory.getType(),
        WinnerActions.updateWinnerSuccess.getType(),
        WinnerActions.updateDrawSuccess.getType(),
        WinnerActions.reselectWinnerSuccess.getType(),
      ],
      fetchWinnersHistoryHandler
    ),
  ]);
}
